/**
 * src/components/PlayHistory.jsx
 * ประวัติการเล่นของ user จาก URL ?user_id=
 */
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchPlayHistory } from '../utils/playApi.js';
import { MOODS } from '../constants/moods.js';

const HISTORY_PREVIEW = 3;
const HISTORY_MAX = 10;

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function bangkokDayKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
}

function daysBetweenBangkok(fromKey, toKey) {
  if (!fromKey || !toKey) return null;
  const from = new Date(`${fromKey}T00:00:00+07:00`);
  const to = new Date(`${toKey}T00:00:00+07:00`);
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

function relativeTag(value) {
  const played = bangkokDayKey(value);
  const today = bangkokDayKey(new Date());
  const diff = daysBetweenBangkok(played, today);
  if (diff == null) return '';
  if (diff <= 0) return 'ล่าสุด';
  if (diff === 1) return 'เมื่อวาน';
  if (diff === 2) return '2 วันที่แล้ว';
  if (diff === 3) return '3 วันที่แล้ว';
  if (diff <= 7) return '7 วันที่แล้ว';
  if (diff <= 30) return 'เดือนที่แล้ว';
  if (diff <= 365) return 'หลายเดือนแล้ว';
  return 'ปีที่แล้ว';
}

function latestTagsByPeriod(sessions) {
  const seen = new Set();
  const tags = new Array(sessions.length).fill('');
  sessions.forEach((session, index) => {
    const tag = relativeTag(session.startedAt);
    if (!tag || seen.has(tag)) return;
    seen.add(tag);
    tags[index] = tag;
  });
  return tags;
}

function formatScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US');
}

function formatPlayDuration(ms) {
  if (ms == null || ms === '') return '';
  const sec = Math.max(0, Math.round(Number(ms) / 1000));
  if (!Number.isFinite(sec)) return '';
  if (sec < 60) return `${sec} วินาที`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  if (min < 60) return rem ? `${min} นาที ${rem} วินาที` : `${min} นาที`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin ? `${hr} ชม. ${remMin} นาที` : `${hr} ชั่วโมง`;
}

function moodMeta(moodId, moodLabel) {
  const mood = MOODS.find((item) => item.id === Number(moodId));
  return {
    label: mood?.label || moodLabel || '',
    color: mood?.color || 'rgba(255, 190, 150, 0.7)',
  };
}

function stopCanvasStart(e) {
  e.stopPropagation();
}

function HistoryList({ sessions, best, expanded, extraCount, total, totalDurationMs, onToggleExpanded }) {
  if (!sessions.length) {
    return <p className="play-history-empty">ยังไม่มีประวัติการเล่น</p>;
  }

  const periodTags = latestTagsByPeriod(sessions);
  const visible = expanded ? sessions : sessions.slice(0, HISTORY_PREVIEW);
  const totalPlayTime = formatPlayDuration(totalDurationMs);

  return (
    <>
      {totalPlayTime && (
        <p className="play-history-summary">
          รวมเวลาเล่น {totalPlayTime}
          {total > 0 ? ` · ${formatScore(total)} ครั้ง` : ''}
        </p>
      )}
      <ul className={`play-history-list${expanded && extraCount > 0 ? ' is-scroll' : ''}`}>
        {visible.map((session, index) => {
          const mood = moodMeta(session.lastMoodId, session.lastMoodLabel);
          const isBest = best > 0 && session.finalScore === best;
          const tag = periodTags[index];
          const playTime = formatPlayDuration(session.durationMs);
          return (
            <li
              key={`${session.startedAt}-${index}`}
              className={`play-history-item${isBest ? ' is-best' : ''}${index === 0 ? ' is-latest' : ''}`}
            >
              <div className="play-history-row">
                <span className="play-history-when">
                  {tag && (
                    <span className={`play-history-tag${tag === 'ล่าสุด' ? ' play-history-tag--now' : ''}`}>
                      {tag}
                    </span>
                  )}
                  {formatWhen(session.startedAt)}
                </span>
                <span className="play-history-score">
                  {isBest && <span className="play-history-best-mark">สูงสุด</span>}
                  {formatScore(session.finalScore)}
                </span>
              </div>
              <div className="play-history-meta">
                <span>ด่าน {session.wavesCompleted || 0}</span>
                {playTime && <span>{playTime}</span>}
                {session.maxCombo > 1 && <span>คอมโบ ×{session.maxCombo}</span>}
                {mood.label && (
                  <span className="play-history-mood" style={{ color: mood.color }}>
                    {mood.label}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {extraCount > 0 && (
        <button
          type="button"
          className="play-history-more"
          aria-label={expanded ? 'แสดง 3 รายการ' : `ดูเพิ่มอีก ${extraCount} รายการ`}
          onClick={() => onToggleExpanded()}
        >
          <span
            className="ui-label"
            data-label={expanded ? 'แสดง 3 รายการ' : `ดูเพิ่มอีก ${extraCount} รายการ`}
            aria-hidden="true"
          />
        </button>
      )}
      {total > HISTORY_MAX && (
        <p className="play-history-footnote">
          แสดงล่าสุด {HISTORY_MAX} จากทั้งหมด {formatScore(total)} ครั้ง
        </p>
      )}
    </>
  );
}

function PlayHistoryInner({ userId, compact = false, dismiss = false, onOpen }) {
  const [open, setOpen] = useState(!compact);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    setOpen(!compact);
    setExpanded(false);
  }, [compact]);

  useEffect(() => {
    if (!dismiss || !compact) return;
    setOpen(false);
  }, [dismiss, compact]);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setLoading(true);
    setExpanded(false);
    fetchPlayHistory(userId, { limit: HISTORY_MAX }).then((res) => {
      if (cancelled) return;
      setData(res);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!compact || !open) return undefined;
    const close = (e) => {
      if (e.target?.closest?.('.play-history, .play-history-pop')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [compact, open]);

  if (!userId) return null;

  const summary = data?.summary || {};
  const sessions = (data?.sessions || []).slice(0, HISTORY_MAX);
  const total = summary.totalSessions || 0;
  const best = summary.bestScore || 0;
  const totalDurationMs = summary.totalDurationMs || 0;
  const extraCount = Math.max(0, sessions.length - HISTORY_PREVIEW);

  const body = (
    <div className="play-history-body">
      {loading && <p className="play-history-empty">กำลังโหลดประวัติ...</p>}
      {!loading && !data && (
        <p className="play-history-empty">โหลดประวัติไม่สำเร็จ</p>
      )}
      {!loading && data && (
        <HistoryList
          sessions={sessions}
          best={best}
          expanded={expanded}
          extraCount={extraCount}
          total={total}
          totalDurationMs={totalDurationMs}
          onToggleExpanded={() => setExpanded((cur) => !cur)}
        />
      )}
    </div>
  );

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((cur) => {
      const next = !cur;
      if (next) onOpen?.();
      return next;
    });
  };

  return (
    <aside
      className={`play-history${compact ? ' play-history--compact' : ''}${open ? ' is-open' : ''}${expanded ? ' is-expanded' : ''}`}
      aria-label="ประวัติการเล่น"
      onPointerDown={stopCanvasStart}
      onClick={stopCanvasStart}
      onTouchStart={stopCanvasStart}
    >
      {compact ? (
        <button
          type="button"
          className={`mute-btn play-history-btn${open ? ' is-open' : ''}`}
          aria-label="ประวัติการเล่น"
          aria-expanded={open}
          title="ประวัติการเล่น"
          onContextMenu={stopCanvasStart}
          onSelectStart={stopCanvasStart}
          onClick={toggle}
        >
          <svg className="mute-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7V3zm.8 4.2v5.1l3.7 2.2-.8 1.3L11 13V7.2h1.8z"
            />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          className="play-history-toggle"
          aria-expanded={open}
          onClick={() => setOpen((cur) => !cur)}
        >
          <span className="play-history-kicker">ประวัติการเล่น</span>
          <span className="play-history-chevron" aria-hidden="true" />
        </button>
      )}

      {open && compact && createPortal(
        <div
          className="play-history-overlay"
          onPointerDown={(e) => {
            e.stopPropagation();
            if (e.target === e.currentTarget) setOpen(false);
          }}
          onClick={stopCanvasStart}
          onTouchStart={stopCanvasStart}
        >
          <div
            className="play-history-pop"
            onPointerDown={stopCanvasStart}
          >
            <p className="play-history-kicker">ประวัติการเล่น</p>
            {body}
          </div>
        </div>,
        document.body,
      )}

      {open && !compact && body}
    </aside>
  );
}

const PlayHistory = memo(PlayHistoryInner);
export default PlayHistory;
